/*
 * ===============================================================================================================
 *                                Copyright 2023, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, Grid, IconButton, Tooltip } from '@mui/material';
import { Trash } from '@jda/lui-common-icon-library-mui5';
import { MRT_Row, MRT_RowData } from 'material-react-table';

import EmptyScreen from './EmptyScreen/EmptyScreen';
import AddBuiltinPoliciesDialog from './AddBuiltinPoliciesDialog/AddBuiltinPoliciesDialog';
import CustomSearch from '../CustomSearch';
import TableWithPaginator from '../../shared/TableWithPaginator';
import Loader from '../../shared/Loader';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import { getEntityPolicyRule } from '../../shared/PolicyDialog/PolicyUtils';
import { fetchGlobalRoleBuiltinPolicies } from '../../store/role/GlobalRole/globalRole';
import { selectBuiltinPoliciesByRoleId } from '../../store/role/GlobalRole/globalRoleSelector';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { selectError } from '../../store/error/errorSelector';
import { IRoleSelected } from '../../models/Roles/RolesContracts';
import { useStyles } from './styles';
import { ADD_BUILTIN_POLICY, SEARCH, LABEL, DELETE, ROWS_PER_PAGE_LABEL, DELETE_SUCCESS_MESSAGE } from './constants';
import { disassociateBuiltinPolicy } from "../../services/policy";
import { setSnackBarMessage } from "../../store/snackBar/snackBarActionCreator";
import interpolateString from '../../utility/stringInterpolation';

const GlobalRoleDetails = ({ selectedItem }: IRoleSelected) => {
  const [searchValue, setSearchValue] = useState('');
  const [isAddBuiltinPoliciesDialogOpen, setIsAddBuiltinPoliciesDialogOpen] = useState(false);

  const policies = useSelector((state: any) => selectBuiltinPoliciesByRoleId(state, selectedItem.id));
  const appLocation = useSelector(selectAppLocation);
  const error = useSelector(selectError);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchGlobalRoleBuiltinPolicies(dispatch, selectedItem, appLocation?.name);
  }, [dispatch, selectedItem, appLocation]);

  const filteredPolicies = useMemo(() => {
    if (!searchValue) {
      return policies;
    }

    const searchValueUpperCased = searchValue.toUpperCase();

    return policies.filter((policy: { displayName: string }) =>
      policy.displayName.toUpperCase().includes(searchValueUpperCased)
    );
  }, [policies, searchValue]);

  const { classes } = useStyles();

  const openAddBuiltinPoliciesDialog = () => {
    setIsAddBuiltinPoliciesDialogOpen(true);
  };

  const closeAddBuiltinPoliciesDialog = () => {
    setIsAddBuiltinPoliciesDialogOpen(false);
  };

  const handleDisassociateBuiltinPolicy = async (policyId: number, displayName: string) => {
    try {
      await disassociateBuiltinPolicy(selectedItem.id, policyId)
      await fetchGlobalRoleBuiltinPolicies(dispatch, selectedItem, appLocation?.name);
      dispatch(setSnackBarMessage({
        showSnackbar: true,
        autoHideDuration: 5000,
        message: interpolateString([`${displayName}`], DELETE_SUCCESS_MESSAGE),
        severity: 'success'
      }))
    } catch (err: any) {
      dispatch(setSnackBarMessage({
        showSnackbar: true,
        autoHideDuration: 5000,
        message: err,
        severity: 'error'
      }))
    }
  };

  const columns = [
    {
      header: LABEL.NAME,
      accessorKey: 'displayName'
    },
    {
      header: LABEL.DESCRIPTION,
      accessorKey: 'description'
    },
    {
      header: LABEL.RULE,
      accessorKey: 'expression',
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => getEntityPolicyRule(JSON.parse(row.original.expression))
    },
    {
      header: '',
      accessorKey: 'delete',
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => appLocation ? null : (
        <Tooltip title={DELETE}>
          <IconButton aria-label={DELETE} onClick={() => handleDisassociateBuiltinPolicy(row.original.id, row.original.displayName)} data-testid='delete-button'>
            <Trash />
          </IconButton>
        </Tooltip>
      ),
      muiTableBodyCellProps: {
        sx: {
          textAlign: 'right'
        }
      }
    }
  ];

  if (!policies) {
    return <Loader showLoader isCenter />;
  }

  if (error.message) {
    return <ErrorMessage {...error} />;
  }

  return (
    <>
      {policies.length === 0 ? (
        <EmptyScreen
          disableCreateAction={Boolean(appLocation)}
          openAddBuiltinPoliciesDialog={openAddBuiltinPoliciesDialog}
          moduleId={selectedItem.moduleId}
        />
      ) : (
        <Box>
          <Grid container className={classes.topPanel}>
            <Grid item xs={4} className={classes.searchContainer}>
              <CustomSearch
                placeHolder={SEARCH}
                value={searchValue}
                onSearchChange={(searchValue: string) => {
                  setSearchValue(searchValue);
                }}
              />
            </Grid>
            {!appLocation && (
              <Grid item xs className={classes.addButtonContainer}>
                <Button onClick={openAddBuiltinPoliciesDialog} variant="contained" color="primary" disableElevation>
                  {ADD_BUILTIN_POLICY}
                </Button>
              </Grid>
            )}
          </Grid>

          <TableWithPaginator
            columns={columns}
            data={filteredPolicies}
            labelRowsPerPage={ROWS_PER_PAGE_LABEL}
            rowsPerPageOptions={[1, 5, 10]}
          />
        </Box>
      )}

      {isAddBuiltinPoliciesDialogOpen && (
        <AddBuiltinPoliciesDialog onClose={closeAddBuiltinPoliciesDialog} selectedItem={selectedItem} />
      )}
    </>
  );
};

export default GlobalRoleDetails;
